// src/operator/panels/resolved-policy-drawer.tsx
//
// Drawer latéral "Aperçu résolu + diff" pour une stratégie host.
//
// Affiche 3 vues pour un host donné :
//   1. Policy DB effective (résolution tenant+site+client+host, avec source
//      par champ).
//   2. Policy LIVE publiée par le host (system_info->hostPolicy).
//   3. Diff champ par champ (match / diverge / db_only / live_only).
//
// Le niveau GPO est lu côté host (registre Windows), jamais en base :
// affiché en lecture seule avec un lien de téléchargement ADMX/ADML pour que
// l'admin AD puisse l'appliquer manuellement via Central Store.
//
// Données fournies par GET /api/host-policy/effective?hostId=...

import { Show, For, createResource, createSignal, type Component } from "solid-js";
import { POLICY_FIELDS_BY_KEY, POLICY_GROUPS, type PolicyObject } from "@/shared/host-policy-schema";
import "./policy-editor.css";

interface DiffEntry {
  key: string;
  dbValue: unknown;
  liveValue: unknown;
  status: "match" | "db_only" | "live_only" | "diverge";
}

interface TraceEntry {
  scope: string;
  scopeId: string | null;
  fields: string[] | null;
}

interface EffectiveResponse {
  dbEffective: PolicyObject;
  sources: Record<string, string>;
  live: PolicyObject | null;
  diff: DiffEntry[];
  trace: TraceEntry[];
}

async function fetchEffective(hostId: string): Promise<EffectiveResponse | null> {
  const r = await fetch(`/api/host-policy/effective?hostId=${encodeURIComponent(hostId)}`, {
    credentials: "include",
  });
  if (!r.ok) return null;
  return r.json() as Promise<EffectiveResponse>;
}

function renderValue(v: unknown): string {
  if (v === undefined || v === null) return "—";
  if (typeof v === "boolean") return v ? "✓" : "✗";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "∅";
  return String(v);
}

function fieldLabel(key: string): string {
  return POLICY_FIELDS_BY_KEY[key]?.label ?? key;
}

function sourceBadge(scope: string | undefined): { cls: string; label: string } | null {
  if (!scope) return null;
  const map: Record<string, string> = {
    tenant: "tenant",
    client: "client",
    site: "site",
    host: "host",
    gpo: "gpo",
  };
  const cls = map[scope] || scope;
  return { cls, label: scope };
}

interface Props {
  hostId: string;
  hostLabel?: string;
  onClose: () => void;
}

const ResolvedPolicyDrawer: Component<Props> = (props) => {
  const [tab, setTab] = createSignal<"diff" | "db" | "live" | "trace">("diff");
  const [effective] = createResource(() => props.hostId, fetchEffective);

  const divergeCount = () => (effective()?.diff || []).filter((d) => d.status !== "match").length;

  const downloadAdmx = (file: string) => {
    window.open(`/api/host-policy/admx?file=${encodeURIComponent(file)}`, "_blank");
  };

  return (
    <div class="policy-drawer-backdrop" onClick={props.onClose}>
      <div class="policy-drawer" onClick={(e) => e.stopPropagation()}>
        <div class="policy-drawer-header">
          <h3>
            Aperçu stratégie résolue —{" "}
            <code style={{ "font-size": "11px", color: "var(--text-3)" }}>{props.hostId}</code>
            <Show when={props.hostLabel}>
              <span style={{ "font-weight": "normal", color: "var(--text-3)", "margin-left": "6px" }}>
                {props.hostLabel}
              </span>
            </Show>
          </h3>
          <button class="btn btn--xs" onClick={props.onClose}>
            ✕ Fermer
          </button>
        </div>

        <div class="policy-drawer-body">
          <Show when={effective()} fallback={<div>Chargement…</div>}>
            {(eff) => (
              <>
                {/* Bandeau GPO (lecture seule) + divergence */}
                <div class="policy-drawer-alert info">
                  <strong>Niveau GPO</strong> : lu côté host depuis <code>HKLM\Software\Policies\RemoteWS\Host</code>{" "}
                  (registre Windows), jamais en base. Non éditable depuis RemoteWS.
                  <div style={{ "margin-top": "6px", display: "flex", gap: "6px", "flex-wrap": "wrap" }}>
                    <button class="btn btn--xs" onClick={() => downloadAdmx("RemoteWSHost.admx")}>
                      ⬇ ADMX
                    </button>
                    <button class="btn btn--xs" onClick={() => downloadAdmx("RemoteWSHost.adml")}>
                      ⬇ ADML (en-US)
                    </button>
                    <span style={{ "font-size": "10px", color: "var(--text-3)", "align-self": "center" }}>
                      Copier dans <code>\\domain\SYSVOL\…\PolicyDefinitions</code> (Central Store AD).
                    </span>
                  </div>
                </div>

                <Show when={divergeCount() > 0}>
                  <div class="policy-drawer-alert">
                    ⚠ {divergeCount()} champ(s) en divergence ou appliqués côté DB mais non encore publiés par le host.
                    Causes possibles : host hors-ligne, prochain heartbeat pas encore effectué, GPO/registre local qui
                    surcharge, ou kill-switch <code>DisableCloudSettings</code> actif côté host.
                  </div>
                </Show>

                {/* Onglets */}
                <div
                  style={{ display: "flex", gap: "2px", "border-bottom": "1px solid var(--border)", margin: "8px 0" }}
                >
                  <For
                    each={[
                      { id: "diff", label: `Diff (${divergeCount()})` },
                      { id: "db", label: "DB effective" },
                      { id: "live", label: "Live publiée" },
                      { id: "trace", label: "Trace" },
                    ]}
                  >
                    {(t) => (
                      <button
                        type="button"
                        onClick={() => setTab(t.id as "diff" | "db" | "live" | "trace")}
                        style={{
                          background: tab() === t.id ? "var(--bg-2)" : "transparent",
                          color: tab() === t.id ? "var(--text-1)" : "var(--text-3)",
                          border: "none",
                          "border-bottom": tab() === t.id ? "2px solid var(--accent)" : "2px solid transparent",
                          padding: "6px 12px",
                          cursor: "pointer",
                          "font-size": "12px",
                        }}
                      >
                        {t.label}
                      </button>
                    )}
                  </For>
                </div>

                <Show when={tab() === "diff"}>
                  <table class="policy-diff-table">
                    <thead>
                      <tr>
                        <th>Champ</th>
                        <th>Statut</th>
                        <th>DB</th>
                        <th>Live</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={eff().diff}>
                        {(d) => (
                          <tr>
                            <td>
                              <div>{fieldLabel(d.key)}</div>
                              <code style={{ "font-size": "10px", color: "var(--text-4)" }}>{d.key}</code>
                            </td>
                            <td>
                              <span class={`policy-diff-status ${d.status}`}>{d.status}</span>
                            </td>
                            <td>
                              <Show
                                when={d.status !== "live_only"}
                                fallback={<span style={{ color: "var(--text-4)" }}>—</span>}
                              >
                                {renderValue(d.dbValue)}
                              </Show>
                            </td>
                            <td>
                              <Show
                                when={d.status !== "db_only"}
                                fallback={<span style={{ color: "var(--text-4)" }}>—</span>}
                              >
                                {renderValue(d.liveValue)}
                              </Show>
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </Show>

                <Show when={tab() === "db"}>
                  <PolicyTableView policy={eff().dbEffective} sources={eff().sources} />
                </Show>

                <Show when={tab() === "live"}>
                  <Show when={eff().live} fallback={<div>Aucune policy live publiée par ce host.</div>}>
                    <PolicyTableView policy={eff().live!} />
                  </Show>
                </Show>

                <Show when={tab() === "trace"}>
                  <div style={{ "margin-bottom": "8px", color: "var(--text-3)", "font-size": "11px" }}>
                    Ordre de précédence :{" "}
                    <code>config.json → local registry → tenant/site/client (DB) → GPO registry → host override</code>.
                    La source la plus haute gagne champ par champ.
                  </div>
                  <For each={eff().trace}>
                    {(t) => (
                      <div class="policy-trace-row">
                        <span class="scope">{t.scope}</span>
                        <span class="fields">
                          <Show when={t.fields && t.fields.length > 0} fallback={<em>(vide)</em>}>
                            {(t.fields || []).join(", ")}
                          </Show>
                        </span>
                      </div>
                    )}
                  </For>
                </Show>
              </>
            )}
          </Show>
        </div>
      </div>
    </div>
  );
};

/** Vue tabulaire d'une policy groupée par thème, avec badge de source optionnel. */
function PolicyTableView(props: { policy: PolicyObject; sources?: Record<string, string> }) {
  const byGroup = () => {
    const groups = new Map<string, { key: string; value: unknown }[]>();
    for (const [key, value] of Object.entries(props.policy)) {
      if (key === "source" || key === "readAt" || key === "precedence" || key === "trace") continue;
      const group = POLICY_FIELDS_BY_KEY[key]?.group ?? "trace";
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group)!.push({ key, value });
    }
    return POLICY_GROUPS.map((g) => ({ group: g, fields: groups.get(g.id) || [] })).filter((g) => g.fields.length > 0);
  };

  return (
    <For each={byGroup()}>
      {(g) => (
        <div style={{ "margin-bottom": "12px" }}>
          <div
            style={{
              "font-size": "11px",
              "text-transform": "uppercase",
              color: "var(--text-3)",
              "margin-bottom": "4px",
              "letter-spacing": "0.5px",
            }}
          >
            {g.group.label}
          </div>
          <For each={g.fields}>
            {(f) => {
              const badge = sourceBadge(props.sources?.[f.key]);
              return (
                <div
                  style={{
                    display: "grid",
                    "grid-template-columns": "1fr auto auto",
                    gap: "8px",
                    padding: "4px 0",
                    "border-bottom": "1px solid rgba(255,255,255,0.03)",
                  }}
                >
                  <span style={{ color: "var(--text-1)" }}>{fieldLabel(f.key)}</span>
                  <Show when={badge}>{(b) => <span class={`policy-source-badge ${b().cls}`}>{b().label}</span>}</Show>
                  <span style={{ color: "var(--text-3)", "font-family": "var(--font-mono)", "font-size": "11px" }}>
                    {renderValue(f.value)}
                  </span>
                </div>
              );
            }}
          </For>
        </div>
      )}
    </For>
  );
}

export default ResolvedPolicyDrawer;
